/**
 * Vendored from `tamoomeh-service/packages/contracts/src/phone.ts`.
 *
 * The Tamoomeh Core API validates a customer request against the same catalog this
 * widget renders, so the two must agree. That copy is canonical; this one is a
 * byte-for-byte copy with only this header added, and `npm run check:contracts`
 * fails when they diverge. Edit Tamoomeh's copy and re-copy, never this file.
 */

/**
 * Phone number handling.
 *
 * Tamoomeh is an Iranian product, so the canonical form is E.164 with the +98
 * country code. Users type all sorts of things (`0912…`, `912…`, `0098…`,
 * with spaces, Persian digits); we normalise on the way in and only ever store
 * and compare the E.164 form.
 */

const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';

/** Convert Persian/Arabic-Indic digits to ASCII and strip formatting. */
export function toAsciiDigits(input: string): string {
  let out = '';
  for (const ch of input) {
    const p = PERSIAN_DIGITS.indexOf(ch);
    if (p >= 0) {
      out += String(p);
      continue;
    }
    const a = ARABIC_DIGITS.indexOf(ch);
    if (a >= 0) {
      out += String(a);
      continue;
    }
    out += ch;
  }
  return out;
}

/**
 * Fold a typed OTP to ASCII digits and drop every non-digit.
 *
 * SMS autofill and paste often include spaces or separators; Persian/Arabic
 * numerals must reach the same value as Latin. The result must still satisfy
 * the 4–8 digit length check — this is normalisation, not acceptance of an
 * empty or truncated code.
 */
export function normalizeOtpCode(raw: string): string {
  if (typeof raw !== 'string') return '';
  return toAsciiDigits(raw).replace(/\D/g, '');
}

export interface NormalizedPhone {
  e164: string;
  countryCode: string;
  national: string;
}

/**
 * Normalise an Iranian mobile number. Returns `null` when the input cannot be
 * interpreted as a valid Iranian mobile number.
 */
export function normalizeIranMobile(raw: string): NormalizedPhone | null {
  if (typeof raw !== 'string') return null;
  const cleaned = toAsciiDigits(raw).replace(/[\s\-()._]/g, '').trim();
  if (!cleaned) return null;

  let national: string | null = null;

  if (/^\+98\d{10}$/.test(cleaned)) {
    national = cleaned.slice(3);
  } else if (/^0098\d{10}$/.test(cleaned)) {
    national = cleaned.slice(4);
  } else if (/^98\d{10}$/.test(cleaned)) {
    national = cleaned.slice(2);
  } else if (/^0\d{10}$/.test(cleaned)) {
    national = cleaned.slice(1);
  } else if (/^\d{10}$/.test(cleaned)) {
    national = cleaned;
  }

  if (!national) return null;
  // Iranian mobile numbers begin with 9 and are 10 digits long.
  if (!/^9\d{9}$/.test(national)) return null;

  return { e164: `+98${national}`, countryCode: '+98', national };
}

export function isValidIranMobile(raw: string): boolean {
  return normalizeIranMobile(raw) !== null;
}

/** Display form used in admin UIs: `0912 345 6789`. */
export function formatIranMobile(e164: string): string {
  const normalized = normalizeIranMobile(e164);
  if (!normalized) return e164;
  const n = normalized.national;
  return `0${n.slice(0, 3)} ${n.slice(3, 6)} ${n.slice(6)}`;
}

/**
 * Mask a phone number for logs and notification records where the full number
 * is not required. Keeps the last 4 digits for support correlation.
 */
export function maskPhone(e164: string): string {
  if (e164.length <= 4) return '****';
  return `${'*'.repeat(Math.max(0, e164.length - 4))}${e164.slice(-4)}`;
}
